import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, openBlock as _openBlock, createBlock as _createBlock, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import { ref, reactive, computed, onMounted, watch } from 'vue'
import dayjs from 'dayjs'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import { PermissionFe, ServicePermissionService } from '@/services/service-permission.service'
import PageSubscribeBar from '@/views/dashboard/_components/subscribe-bar/page-subscribe-bar.vue'
import StatisticsCards from './_components/statistics-cards.vue'
import PopupInterceptTrendChart from './_components/popup-intercept-trend-chart.vue'
import PopupInterceptRankingChart from './_components/popup-intercept-ranking-chart.vue'
import PopupSoftwareList from './_components/popup-software-list.vue'
import type { PopupSoftwareListEmbedDefaults } from './_components/popup-view/popup-view.service'
import TerminalList from './_components/terminal-list.vue'
import TrustList from './_components/trust-list.vue'
import Card from '@/views/dashboard/_components/card/card.vue'
import { TableViewMode } from '@/views/dashboard/_enum/table-view-mode'
import { TimeRange } from '@/views/dashboard/_components/time-range-select/time-range.enum'
import { WhiteListViewService } from './_components/whiteList-view/whiteList-view.service'
import { PopupInterceptService, adpopupinterceptTrendStatRequestParams, adpopupinterceptTopSoftStatRequestParams } from './service'
import type { PopupInterceptStatCardKey } from './_components/popup-intercept-statistics-cards.types'


export default /*#__PURE__*/_defineComponent({
  __name: 'popup-intercept',
  setup(__props) {

const injector = useReflectiveInjector([ WhiteListViewService, PopupInterceptService])
const servicePermissionService = injector.get(ServicePermissionService)
const whiteListViewService = injector.get(WhiteListViewService)
const popupInterceptService = injector.get(PopupInterceptService)
const hasPermission = servicePermissionService.getHasPermission(PermissionFe.popwindow_block)

// 统计数据
const statistics = reactive({
  totalIntercepted: 0, // 累计拦截
  todayIntercepted: 0, // 今日拦截
  adPopupSoftwareCount: 0, // 广告弹窗软件数量
  involvedTerminals: 0 // 涉及终端
})

// 图表数据
const trendTimeRange = ref<TimeRange>(TimeRange.DAYS_30)
const rankingTimeRange = ref<TimeRange>(TimeRange.DAYS_30)
const trendChartData = ref({
  times: [] as string[],
  counts: [] as number[]
})

const rankingData = ref<Array<{ name: string; count: number }>>([])

// 组件引用
const popupRankingChartRef = ref()

// 抽屉状态
const popupDrawerVisible = ref(false)
const popupSoftwareEmbedDefaults = ref<PopupSoftwareListEmbedDefaults | null>(null)
/** 每次打开抽屉递增，避免与排行相同筛参时子组件不重建、列表未按 embed 请求 */
const popupSoftwareDrawerNonce = ref(0)
const popupSoftwareDrawerListKey = computed(() => {
  const d = popupSoftwareEmbedDefaults.value
  const n = popupSoftwareDrawerNonce.value
  if (!d) return `popup-soft-all-${n}`
  return `popup-soft-${d.key_word ?? ''}-${d.start_time ?? ''}-${d.end_time ?? ''}-${n}`
})
const terminalDrawerVisible = ref(false)
const trustDrawerVisible = ref(false)

// 获取时间范围
function getTimeRange(range: TimeRange) {
  const now = new Date()
  let startTime: Date
  let endTime = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999)

  switch (range) {
    case TimeRange.HOURS_24:
      startTime = new Date(now.getTime() - 24 * 60 * 60 * 1000)
      break
    case TimeRange.DAYS_7:
      startTime = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
      break
    case TimeRange.DAYS_30:
      startTime = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
      break
    case TimeRange.ALL:
    default:
      // 全部时间，可以设置一个较早的日期，或者根据实际需求调整
      startTime = new Date(2020, 0, 1)
      break
  }

  return {
    start_time: Math.floor(startTime.getTime() / 1000).toString(),
    end_time: Math.floor(endTime.getTime() / 1000).toString()
  }
}

// 加载统计数据
async function loadStatistics() {
  if (!hasPermission) return
  try {
    const res = await popupInterceptService.getAdpopupinterceptOverviewStat()
    statistics.totalIntercepted = res.total_intercept_count || 0
    statistics.todayIntercepted = res.period_intercept_count || 0
    statistics.adPopupSoftwareCount = res.ad_popup_soft_count || 0
    statistics.involvedTerminals = res.involved_terminal_count || 0
  } catch (error) {
    console.error('加载统计数据失败:', error)
  }
}

// 加载趋势图数据
async function loadTrendData() {
  if (!hasPermission) return
  try {
    const timeRange = getTimeRange(trendTimeRange.value)
    const granularity = trendTimeRange.value === TimeRange.HOURS_24 ? 'hour' : 'day'

    const params: adpopupinterceptTrendStatRequestParams = {
      start_time: timeRange.start_time,
      end_time: timeRange.end_time,
      granularity
    }

    const res = await popupInterceptService.getAdpopupinterceptTrendStat(params)

    // 转换时间戳为可读格式
    const times = res.timestamps.map(ts => {
      const date = new Date(Number(ts) * 1000)
      if (granularity === 'hour') {
        return `${date.getMonth() + 1}/${date.getDate()} ${date.getHours()}:00`
      } else {
        return `${date.getMonth() + 1}/${date.getDate()}`
      }
    })

    trendChartData.value = {
      times,
      counts: res.intercept_counts || []
    }
  } catch (error) {
    console.error('加载趋势图数据失败:', error)
    trendChartData.value = {
      times: [],
      counts: []
    }
  }
}

// 加载排行数据
async function loadRankingData() {
  if (!hasPermission) return
  const rankingChartRef = popupRankingChartRef.value
  if (rankingChartRef) {
    rankingChartRef.loading = true
  }
  try {
    const timeRange = getTimeRange(rankingTimeRange.value)
    const params: adpopupinterceptTopSoftStatRequestParams = {
      start_time: timeRange.start_time,
      end_time: timeRange.end_time
    }

    const res = await popupInterceptService.getAdpopupinterceptTopSoftStat(params)

    rankingData.value = (res.top_soft_list || []).map(item => ({
      name: item.soft_name,
      count: item.intercept_count
    }))
  } catch (error) {
    console.error('加载排行数据失败:', error)
    rankingData.value = []
  } finally {
    if (rankingChartRef) {
      rankingChartRef.loading = false
    }
  }
}

// 处理信任列表选择变化
function bumpPopupSoftwareDrawerNonce () {
  popupSoftwareDrawerNonce.value += 1
}

// 查看全部弹窗软件
function viewAllPopupSoftware() {
  if (!hasPermission) return
  popupSoftwareEmbedDefaults.value = null
  bumpPopupSoftwareDrawerNonce()
  popupDrawerVisible.value = true
}

/** 排行项点击：打开 PopupSoftwareList 抽屉，带入软件关键词与排行图当前时间范围 */
function handleRankingSoftwareItemClick(item: { name?: string; count?: number }) {
  if (!hasPermission) return
  const count = item?.count ?? 0
  if (count <= 0) return
  const name = (item?.name ?? '').trim()
  if (!name) return
  const { start_time, end_time } = getTimeRange(rankingTimeRange.value)
  popupSoftwareEmbedDefaults.value = {
    key_word: name,
    start_time,
    end_time,
    dateRange: [
      dayjs.unix(Number(start_time)).format('YYYY-MM-DD'),
      dayjs.unix(Number(end_time)).format('YYYY-MM-DD'),
    ] as [string, string],
  }
  bumpPopupSoftwareDrawerNonce()
  popupDrawerVisible.value = true
}

// 查看全部终端
function viewAllTerminals() {
  if (!hasPermission) return
  terminalDrawerVisible.value = true
}

function viewAllTrustList() {
  if (!hasPermission) return
  trustDrawerVisible.value = true
}

/** 顶栏统计卡：弹窗软件 / 涉及终端侧栏（statistics-events） */
function handlePopupInterceptStatClick(key: PopupInterceptStatCardKey) {
  if (key === 'adPopupSoftware') {
    if (statistics.adPopupSoftwareCount <= 0) return
    popupSoftwareEmbedDefaults.value = null
    bumpPopupSoftwareDrawerNonce()
    popupDrawerVisible.value = true
    return
  }
  if (statistics.involvedTerminals <= 0) return
  terminalDrawerVisible.value = true
}

// 时间范围与趋势图请求同步（子组件 v-model:time-range 更新后触发）
watch(trendTimeRange, () => {
  loadTrendData()
})

watch(rankingTimeRange, () => {
  loadRankingData()
})

onMounted(() => {
  if (hasPermission) {
    loadStatistics()
    loadTrendData()
    loadRankingData()
  }
})

return (_ctx: any,_cache: any) => {
  const _component_el_col = _resolveComponent("el-col")!
  const _component_el_row = _resolveComponent("el-row")!
  const _component_el_tag = _resolveComponent("el-tag")!
  const _component_el_drawer = _resolveComponent("el-drawer")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(PageSubscribeBar, {
      "normal-permission-key": _unref(PermissionFe).popwindow_block
    }, null, 8, ["normal-permission-key"]),
    _createVNode(StatisticsCards, {
      "total-intercepted": statistics.totalIntercepted,
      "today-intercepted": statistics.todayIntercepted,
      "ad-popup-software-count": statistics.adPopupSoftwareCount,
      "involved-terminals": statistics.involvedTerminals,
      onStatClick: handlePopupInterceptStatClick
    }, null, 8, ["total-intercepted", "today-intercepted", "ad-popup-software-count", "involved-terminals"]),
    _createVNode(_component_el_row, {
      gutter: 16,
      class: "cm-ui-mt16"
    }, {
      default: _withCtx(() => [
        _createVNode(_component_el_col, {
          span: 18,
          style: {"height":"360px"}
        }, {
          default: _withCtx(() => [
            _createVNode(PopupInterceptTrendChart, {
              "time-range": trendTimeRange.value,
              "onUpdate:timeRange": _cache[0] || (_cache[0] = ($event: any) => ((trendTimeRange).value = $event)),
              data: trendChartData.value
            }, null, 8, ["time-range", "data"])
          ]),
          _: 1
        }),
        _createVNode(_component_el_col, { span: 6 }, {
          default: _withCtx(() => [
            _createVNode(PopupInterceptRankingChart, {
              ref_key: "popupRankingChartRef",
              ref: popupRankingChartRef,
              "time-range": rankingTimeRange.value,
              "onUpdate:timeRange": _cache[1] || (_cache[1] = ($event: any) => ((rankingTimeRange).value = $event)),
              data: rankingData.value,
              onItemClick: handleRankingSoftwareItemClick
            }, null, 8, ["time-range", "data"])
          ]),
          _: 1
        })
      ]),
      _: 1
    }),
    _createVNode(Card, {
      size: "second",
      title: _ctx.$t('popupIntercept.softwareTop10'),
      class: "cm-ui-mt16 cm-ui-mb16"
    }, {
      titleAfter: _withCtx(() => [
        _createVNode(_component_el_tag, { size: "small" }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_ctx.$t('common.listPreviewRecent10')), 1)
          ]),
          _: 1
        })
      ]),
      default: _withCtx(() => [
        _createVNode(PopupSoftwareList, {
          "view-mode": _unref(TableViewMode).preview,
          "has-permission": _unref(hasPermission),
          onViewAll: viewAllPopupSoftware,
          onTrustSuccess: loadStatistics
        }, null, 8, ["view-mode", "has-permission"])
      ]),
      _: 1
    }, 8, ["title"]),
    _createVNode(Card, {
      size: "second",
      title: _ctx.$t('popupIntercept.terminalTop10'),
      class: "cm-ui-mt16 cm-ui-mb16"
    }, {
      titleAfter: _withCtx(() => [
        _createVNode(_component_el_tag, { size: "small" }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_ctx.$t('common.listPreviewRecent10')), 1)
          ]),
          _: 1
        })
      ]),
      default: _withCtx(() => [
        _createVNode(TerminalList, {
          "view-mode": _unref(TableViewMode).preview,
          "has-permission": _unref(hasPermission),
          onViewAll: viewAllTerminals
        }, null, 8, ["view-mode", "has-permission"])
      ]),
      _: 1
    }, 8, ["title"]),
    _createVNode(Card, {
      size: "second",
      title: _ctx.$t('popupIntercept.trustList'),
      class: "cm-ui-mt16 cm-ui-mb16"
    }, {
      default: _withCtx(() => [
        _createVNode(TrustList, {
          "view-mode": _unref(TableViewMode).preview,
          "has-permission": _unref(hasPermission),
          onViewAll: viewAllTrustList,
          onUntrustSuccess: loadStatistics
        }, null, 8, ["view-mode", "has-permission"])
      ]),
      _: 1
    }, 8, ["title"]),
    _createVNode(_component_el_drawer, {
      size: "80%",
      title: _ctx.$t('popupIntercept.allSoftware'),
      modelValue: popupDrawerVisible.value,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((popupDrawerVisible).value = $event)),
      "destroy-on-close": ""
    }, {
      default: _withCtx(() => [
        (_openBlock(), _createBlock(PopupSoftwareList, {
          key: popupSoftwareDrawerListKey.value,
          "view-mode": _unref(TableViewMode).full,
          "has-permission": _unref(hasPermission),
          "embed-defaults": popupSoftwareEmbedDefaults.value
        }, null, 8, ["view-mode", "has-permission", "embed-defaults"]))
      ]),
      _: 1
    }, 8, ["title", "modelValue"]),
    _createVNode(_component_el_drawer, {
      size: "80%",
      title: _ctx.$t('popupIntercept.allTerminalList'),
      modelValue: terminalDrawerVisible.value,
      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((terminalDrawerVisible).value = $event)),
      "destroy-on-close": ""
    }, {
      default: _withCtx(() => [
        _createVNode(TerminalList, {
          "view-mode": _unref(TableViewMode).full,
          "has-permission": _unref(hasPermission)
        }, null, 8, ["view-mode", "has-permission"])
      ]),
      _: 1
    }, 8, ["title", "modelValue"]),
    _createVNode(_component_el_drawer, {
      size: "80%",
      title: _ctx.$t('popupIntercept.allTrustList'),
      modelValue: trustDrawerVisible.value,
      "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((trustDrawerVisible).value = $event)),
      "destroy-on-close": ""
    }, {
      default: _withCtx(() => [
        _createVNode(TrustList, {
          "view-mode": _unref(TableViewMode).full,
          "has-permission": _unref(hasPermission),
          onUntrustSuccess: loadStatistics
        }, null, 8, ["view-mode", "has-permission"])
      ]),
      _: 1
    }, 8, ["title", "modelValue"])
  ], 64))
}
}

})